/**
 * Hybrid live editing (decisions 2, 26; ADR 2): what the editor shows for each construct of the chapter tree,
 * as plain data. Markup the writer isn't working on is hidden and its text styled, so the chapter reads as
 * formatted prose; a construct the selection touches shows its markup, so the writer can see and edit what they
 * are on. Escapes and entities always show as the character they stand for (decision 66), and the cursor moves
 * over each as one character. Table D in the part 09 log; one test per row.
 *
 * Decorations never change the text: they are a pure function of the tree, the text, the range on screen and the
 * selection. The CodeMirror wiring is `view.ts`.
 */
import { decodeEntity } from '@cockatrice/core';
import type { Text } from '@codemirror/state';
import type { SyntaxNode, SyntaxNodeRef, Tree } from '@lezer/common';

/** The class names the editor's theme styles; each names the construct, not its look. */
export const LIVE_CLASS = {
  /** Shown markup of a touched construct (D4–D9, D11, D14) and markup that never hides (D10, D12, D15). */
  markup: 'ct-markup',
  emphasis: 'ct-emphasis',
  strong: 'ct-strong',
  smallCaps: 'ct-small-caps',
  link: 'ct-link',
  image: 'ct-image',
  footnoteReference: 'ct-footnote-reference',
  footnoteLabel: 'ct-footnote-label',
  footnote: 'ct-footnote',
  heading: 'ct-heading',
  quote: 'ct-quote',
  listMark: 'ct-list-mark',
  centered: 'ct-centered',
  sceneBreak: 'ct-scene-break',
  linkDefinition: 'ct-link-definition',
  entity: 'ct-entity',
  escape: 'ct-escape',
  bullet: 'ct-bullet',
} as const;

/** What a scene break shows when it isn't touched (D13). */
export const SCENE_BREAK_ORNAMENT = '* * *';
/** What a bullet list mark shows when its line isn't touched (D12). */
export const BULLET = '•';

/** One decoration, as data. Ranges are source offsets; a line decoration sits at its line's start. */
export type LiveDecoration =
  /** The range shows nothing. */
  | { readonly kind: 'hide'; readonly from: number; readonly to: number }
  /** The range shows its text with `className`. */
  | { readonly kind: 'mark'; readonly from: number; readonly to: number; readonly className: string }
  /** The line starting at `at` has `className`. */
  | { readonly kind: 'line'; readonly at: number; readonly className: string }
  /** The range shows `text` instead, with `className`. */
  | { readonly kind: 'widget'; readonly from: number; readonly to: number; readonly text: string; readonly className: string };

/** A source range the cursor moves over, and deletion removes, as one character (D2, D3, D17). */
export interface AtomicRange {
  readonly from: number;
  readonly to: number;
}

/** A range of the source: what is on screen, or one selection range. */
export interface SourceRange {
  readonly from: number;
  readonly to: number;
}

/** The decorations for one range of a chapter. */
export interface LiveView {
  /** In the order the tree is walked; `view.ts` sorts them for CodeMirror. */
  readonly decorations: readonly LiveDecoration[];
  readonly atomic: readonly AtomicRange[];
}

/** The level of each heading node. */
const HEADING_LEVEL: Readonly<Partial<Record<string, number>>> = {
  ATXHeading1: 1,
  ATXHeading2: 2,
  ATXHeading3: 3,
  ATXHeading4: 4,
  ATXHeading5: 5,
  ATXHeading6: 6,
  SetextHeading1: 1,
  SetextHeading2: 2,
};

/** Inline constructs styled as a whole, and their delimiter nodes. */
const INLINE_MARKS: Readonly<Partial<Record<string, { readonly className: string; readonly mark: string }>>> = {
  Emphasis: { className: LIVE_CLASS.emphasis, mark: 'EmphasisMark' },
  StrongEmphasis: { className: LIVE_CLASS.strong, mark: 'EmphasisMark' },
  SmallCaps: { className: LIVE_CLASS.smallCaps, mark: 'SmallCapsMark' },
};

/** A bullet list's marks; an ordered list's are digits and `.` or `)`. */
const BULLET_MARKS: ReadonlySet<string> = new Set(['-', '+', '*']);

const SPACE = 32;
const TAB = 9;
const isBlank = (code: number): boolean => code === SPACE || code === TAB;

/** Builds one range's decorations; see {@link liveDecorations}. */
class Builder {
  readonly decorations: LiveDecoration[] = [];
  readonly atomic: AtomicRange[] = [];
  /** How many quotes the walk is inside. */
  private quoteDepth = 0;
  private readonly doc: Text;
  private readonly range: SourceRange;
  private readonly selection: readonly SourceRange[];

  constructor(doc: Text, range: SourceRange, selection: readonly SourceRange[]) {
    this.doc = doc;
    this.range = range;
    this.selection = selection;
  }

  /** Whether a selection range meets `from`–`to`, ends included (D21). */
  private touches(from: number, to: number): boolean {
    return this.selection.some((r) => r.from <= to && r.to >= from);
  }

  /** Whether a selection range meets the line holding `pos`. */
  private touchesLine(pos: number): boolean {
    const line = this.doc.lineAt(pos);
    return this.touches(line.from, line.to);
  }

  private hide(from: number, to: number): void {
    this.decorations.push({ kind: 'hide', from, to });
  }

  private mark(from: number, to: number, className: string): void {
    if (from < to) this.decorations.push({ kind: 'mark', from, to, className });
  }

  /** Markup that hides unless `shown`, when it shows as markup. */
  private markup(from: number, to: number, shown: boolean): void {
    if (shown) this.mark(from, to, LIVE_CLASS.markup);
    else this.hide(from, to);
  }

  /** A line decoration on every line of `from`–`to` that is in the range. */
  private lines(from: number, to: number, className: string): void {
    const start = Math.max(from, this.range.from);
    const end = Math.min(to, this.range.to);
    if (start > end) return;
    let line = this.doc.lineAt(start);
    for (;;) {
      this.decorations.push({ kind: 'line', at: line.from, className });
      if (line.to >= end) return;
      line = this.doc.line(line.number + 1);
    }
  }

  /** `to` moved past the blanks that follow it, up to `limit`. */
  private blanksAfter(to: number, limit: number): number {
    let end = to;
    while (end < limit && isBlank(this.doc.sliceString(end, end + 1).charCodeAt(0))) end += 1;
    return end;
  }

  /** `from` moved back over the blanks before it, down to `limit`. */
  private blanksBefore(from: number, limit: number): number {
    let start = from;
    while (start > limit && isBlank(this.doc.sliceString(start - 1, start).charCodeAt(0))) start -= 1;
    return start;
  }

  /**
   * Called for each node the walk enters. The walk goes into every node: a construct decorates its own marks, so the
   * marks it holds add nothing when entered.
   */
  enter(ref: SyntaxNodeRef): void {
    const { name, from, to } = ref;
    const level = HEADING_LEVEL[name];
    const inline = INLINE_MARKS[name];
    if (level !== undefined) {
      this.heading(ref.node, level);
    } else if (inline !== undefined) {
      // D4, D5: the content shows styled; the delimiters hide unless the construct is touched.
      this.mark(from, to, inline.className);
      const shown = this.touches(from, to);
      for (const mark of ref.node.getChildren(inline.mark)) this.markup(mark.from, mark.to, shown);
    } else {
      this.other(ref);
    }
  }

  /** Every other construct table D decorates. */
  private other(ref: SyntaxNodeRef): void {
    const { name, from, to } = ref;
    switch (name) {
      case 'Escape':
        // D2, W1: the escape shows as the character it stands for, one widget, so no position lies inside it.
        this.decorations.push({ kind: 'widget', from, to, text: this.doc.sliceString(from + 1, to), className: LIVE_CLASS.escape });
        this.atomic.push({ from, to });
        break;
      case 'Entity':
        this.entity(from, to);
        break;
      case 'HardBreak':
        // D17, W3: a backslash break's `\` is markup, like a heading's `#`: hidden unless its line is touched, so the
        // writer sees every position on the line they are on. W4: a two-space break shows as written.
        if (this.doc.sliceString(from, from + 1) === '\\') this.markup(from, from + 1, this.touchesLine(from));
        break;
      case 'Link':
        this.link(ref.node, 1, LIVE_CLASS.link);
        break;
      case 'Image':
        this.link(ref.node, 2, LIVE_CLASS.image);
        break;
      case 'FootnoteReference':
        this.footnoteReference(ref.node);
        break;
      case 'FootnoteDefinition':
        // D15: a note definition's lines are set apart, and its own label always shows.
        this.lines(from, to, LIVE_CLASS.footnote);
        for (const label of [...ref.node.getChildren('FootnoteMark'), ...ref.node.getChildren('FootnoteLabel')]) {
          this.mark(label.from, label.to, LIVE_CLASS.footnoteLabel);
        }
        break;
      case 'Blockquote':
        // D11: an outermost quote's lines are set apart; a quote inside it adds nothing, so no line has the class twice.
        if (this.quoteDepth === 0) this.lines(from, to, LIVE_CLASS.quote);
        this.quoteDepth += 1;
        break;
      case 'QuoteMark':
        // D11: the mark and one blank after it hide unless the line is touched.
        this.markup(from, this.blanksAfter(to, Math.min(to + 1, this.doc.length)), this.touchesLine(from));
        break;
      case 'ListMark':
        this.listMark(from, to);
        break;
      case 'SceneBreak':
        // D13: an ornament unless touched.
        this.lines(from, to, LIVE_CLASS.sceneBreak);
        if (this.touches(from, to)) this.mark(from, to, LIVE_CLASS.markup);
        else this.decorations.push({ kind: 'widget', from, to, text: SCENE_BREAK_ORNAMENT, className: LIVE_CLASS.sceneBreak });
        break;
      case 'CenteredBlock':
        this.centered(ref.node);
        break;
      case 'LinkReference':
        // D16: a link reference definition shows as it is, set apart.
        this.lines(from, to, LIVE_CLASS.linkDefinition);
        break;
    }
  }

  /** Called for each node the walk leaves. */
  leave(ref: SyntaxNodeRef): void {
    if (ref.name === 'Blockquote') this.quoteDepth -= 1;
  }

  /** D3: a known entity shows as its character; an unknown one is text. */
  private entity(from: number, to: number): void {
    const character = decodeEntity(this.doc.sliceString(from, to));
    if (character === null) return;
    this.decorations.push({ kind: 'widget', from, to, text: character, className: LIVE_CLASS.entity });
    this.atomic.push({ from, to });
  }

  /**
   * D9, D10: a heading's lines are styled by level. An ATX heading's opening `#`s with the blanks after them, and a
   * closing sequence with the blanks before it, hide unless the line is touched; a setext underline always shows,
   * as markup.
   */
  private heading(heading: SyntaxNode, level: number): void {
    const className = `${LIVE_CLASS.heading} ${LIVE_CLASS.heading}-${level}`;
    const marks = heading.getChildren('HeaderMark');
    if (heading.name.startsWith('Setext')) {
      // The underline is the last line, and the heading's text the lines before it.
      for (const mark of marks) {
        this.lines(heading.from, mark.from - 1, className);
        this.mark(mark.from, mark.to, LIVE_CLASS.markup);
      }
      return;
    }
    this.lines(heading.from, heading.to, className);
    this.openClose(marks, heading.to, this.touchesLine(heading.from));
  }

  /**
   * D6, D7: in an inline link or image, the text between `[` (or `![`, `open` characters) and `]` shows styled; the
   * opening mark and everything from `]` to the end (destination and title) hide unless the construct is touched.
   * The parts after `]` are leaves, so nothing inside them is decorated. A reference form (`[text][label]`,
   * `[text][]`, `[text]`) has no `(` and shows as it is written (D6b): whether it resolves depends on definitions
   * anywhere in the chapter, and the stored form writes every resolved link inline and escapes the rest (part 04),
   * so only text not yet stored has one.
   */
  private link(node: SyntaxNode, open: number, className: string): void {
    // An inline form's third mark is the `(` right after `]`.
    const paren = node.getChildren('LinkMark')[2];
    if (paren === undefined) return;
    const close = paren.from - 1;
    this.mark(node.from + open, close, className);
    // D6c: a destination or title after a line end shows, touched or not: hiding it would hide a line end, which
    // CodeMirror refuses from a view plugin (deep-fix r1).
    const shown = this.touches(node.from, node.to) || this.doc.lineAt(close).to < node.to;
    this.markup(node.from, node.from + open, shown);
    this.markup(close, node.to, shown);
  }

  /** D8: a note reference shows its label as a superscript; `[^` and `]` hide unless it is touched. */
  private footnoteReference(node: SyntaxNode): void {
    const shown = this.touches(node.from, node.to);
    for (let child = node.firstChild; child !== null; child = child.nextSibling) {
      if (child.name === 'FootnoteLabel') this.mark(child.from, child.to, LIVE_CLASS.footnoteReference);
      else this.markup(child.from, child.to, shown);
    }
  }

  /** D12: a bullet (`-`, `+`, `*`) shows as a dot unless its line is touched; an ordered list's number always shows. */
  private listMark(from: number, to: number): void {
    if (BULLET_MARKS.has(this.doc.sliceString(from, to)) && !this.touchesLine(from)) {
      this.decorations.push({ kind: 'widget', from, to, text: BULLET, className: LIVE_CLASS.bullet });
    } else {
      this.mark(from, to, LIVE_CLASS.listMark);
    }
  }

  /**
   * D14: a centered block's lines are centered; `->` with the blanks after it and `<-` with the blanks before it
   * hide unless the block is touched.
   */
  private centered(block: SyntaxNode): void {
    this.lines(block.from, block.to, LIVE_CLASS.centered);
    this.openClose(block.getChildren('CenterMark'), block.to, this.touches(block.from, block.to));
  }

  /**
   * An opening mark with the blanks after it, and a closing mark with the blanks before it, as markup (D9, D14). The
   * blanks between two marks with nothing else between them go with the opening one, so the two never overlap.
   */
  private openClose(marks: readonly SyntaxNode[], end: number, shown: boolean): void {
    let openEnd = end;
    marks.forEach((mark, index) => {
      if (index === 0) {
        openEnd = this.blanksAfter(mark.to, marks[1]?.from ?? end);
        this.markup(mark.from, openEnd, shown);
      } else {
        this.markup(this.blanksBefore(mark.from, openEnd), mark.to, shown);
      }
    });
  }
}

/**
 * The decorations of the part of `tree` that meets `range`, with `selection` revealing what it touches (table D).
 * Pure. `tree` must be the tree of `doc`. Only nodes that meet `range` are visited, and line decorations are
 * given only for lines in it, so the cost follows what is on screen, not the chapter's length (decision 14).
 */
export function liveDecorations(tree: Tree, doc: Text, range: SourceRange, selection: readonly SourceRange[]): LiveView {
  const builder = new Builder(doc, range, selection);
  tree.iterate({
    from: range.from,
    to: range.to,
    enter: (node) => {
      builder.enter(node);
    },
    leave: (node) => {
      builder.leave(node);
    },
  });
  return { decorations: builder.decorations, atomic: builder.atomic };
}
